<template>
    <div>
        <DeveloperTools v-if="isAdmin" />
        <Presence v-if="currentAccount" :channel="'Account.' + currentAccount" :hidden="true" />
        <Presence v-if="currentEvent" :channel="'Event.' + currentEvent" :hidden="true" />
    </div>
</template>
<script>
import Pusher from 'pusher-js';
import { useDataStore } from '@/js/stores/DataStore.js';
import debounce from 'lodash/debounce';
import Presence from '@/js/components/widgets/PresenceIndicator.vue';
import DeveloperTools from '@/js/components/modals/DeveloperTools.vue';
import { toast } from '@/js/utils.js';
import { recalculateDepartment, recalculateRevenueDepartment } from '@/js/composables/useRecalculate.js';
import { sockets } from '@/js/composables/useSockets.js';
import { useLogs } from '@/js/composables/useLogs.ts';

const { log } = useLogs();

export default {
    components: {
        DeveloperTools,
        Presence,
    },
    data() {
        const store = useDataStore();
        return {
            isAdmin: false,
            store: store,
            debug: false,
            currentAccount: null,
            currentEvent: null,
            refreshInterval: null,
            log: log,
        };
    },
    mounted() {
        if (this.store.get('current_user.is_admin') || this.store.get('current_user.is_super_admin')) {
            this.isAdmin = true;
        }

        if (this.store.get('disable_notifications')) {
            return;
        }

        if (this.store.get('environment') === 'local') {
            this.debug = true;
            Pusher.logToConsole = false;
        }

        Eventbus.$on('Private:join', this.joinChannel);
        Eventbus.$on('Private:leave', this.leaveChannel);
        Eventbus.$on('Presence:join', this.joinPresence);
        Eventbus.$on('Presence:leave', this.leavePresence);
        Eventbus.$on('forceRefetchEvent', () => this.forceRefetchEvent());
        Eventbus.$on('enablePusherLog', () => (Pusher.logToConsole = true));

        const currentUser = this.store.get('current_user');
        if (currentUser) {
            sockets
                .joinChannel(`User.${currentUser.id}`)
                .listen('.toast', (e) => {
                    toast(e.title, e.message, e.type);
                })
                .listen('.update', (e) => {
                    this.storeUpdate(e);
                })
                .listen('.delete', (e) => {
                    this.storeDelete(e);
                })
                .listen('.eventbus', (e) => {
                    Eventbus.$emit(e.emitter, e.data);
                })
                .listen('.rolechange', (e) => {
                    if (e && e.account && e.account === this.store.get('account.id')) {
                        swal.fire({
                            title: 'Role Changed',
                            text: 'Your assigned role has changed and you must reload the page in order to continue.',
                            allowOutsideClick: false,
                        }).then(() => location.reload());
                    }
                })
                .listen('.reload', (e) => {
                    const body = document.getElementById('app');
                    body.style.cssText += '-webkit-filter: blur(5px);';
                    if (e && e.message) {
                        swal.fire({
                            title: e.message.title,
                            text: e.message.text,
                            allowOutsideClick: false,
                        }).then(() => location.reload());
                    } else {
                        location.reload();
                    }
                })
                .listen('.refresh', (e) => {
                    if (e && e.account && e.account === this.store.get('account.id')) {
                        this.forceRefetchEvent();
                    }
                })
                .notification((e) => {
                    Eventbus.$emit('reloadNotifications', e);
                    Eventbus.$emit('reloadNotificationsPage', e);
                });
        }

        this.currentAccount = this.store.get('account.id');
        this.currentEvent = this.store.get('current_event.id');
        if (this.currentEvent) {
            sockets
                .joinChannel(`Event.${this.currentEvent}`)
                .listen('.toast', (e) => {
                    toast(e.title, e.message, e.type);
                })
                .listen('.update', (e) => {
                    this.storeUpdate(e);
                })
                .listen('.delete', (e) => {
                    this.storeDelete(e);
                })
                .listen('.eventbus', (e) => {
                    Eventbus.$emit(e.emitter, e.data);
                })
                .listen('.refresh', () => {
                    this.refetchEvent();
                });
            if (currentUser) {
                sockets
                    .joinChannel(`Event.${this.currentEvent}.User.${currentUser.id}`)
                    .listen('.toast', (e) => {
                        toast(e.title, e.message, e.type);
                    })
                    .listen('.update', (e) => {
                        this.storeUpdate(e);
                    })
                    .listen('.delete', (e) => {
                        this.storeDelete(e);
                    })
                    .listen('.eventbus', (e) => {
                        Eventbus.$emit(e.emitter, e.data);
                    })
                    .listen('.refresh', () => {
                        this.refetchEvent();
                    });
            }
            this.refreshInterval = setInterval(() => this.refetchEvent(), 1200000);
        }
        if (this.currentAccount) {
            sockets
                .joinChannel(`Account.${this.currentAccount}`)
                .listen('.toast', (e) => {
                    toast(e.title, e.message, e.type);
                })
                .listen('.update', (e) => {
                    this.storeUpdate(e);
                })
                .listen('.delete', (e) => {
                    this.storeDelete(e);
                })
                .listen('.eventbus', (e) => {
                    Eventbus.$emit(e.emitter, e.data);
                })
                .listen('.refresh', () => {
                    this.refetchAccount();
                });
        }
    },
    methods: {
        joinPresence(channelId) {
            this.debugLog('Joining presence channel: ' + channelId);
            sockets
                .joinPresenceChannel(channelId)
                .here((users) => {
                    Eventbus.$emit('Presence-' + channelId, {
                        event: 'here',
                        users: users,
                    });
                })
                .joining((user) => {
                    Eventbus.$emit('Presence-' + channelId, {
                        event: 'joining',
                        user: user,
                    });
                })
                .leaving((user) => {
                    Eventbus.$emit('Presence-' + channelId, {
                        event: 'leaving',
                        user: user,
                    });
                })
                .listenToAll((e) => {
                    Eventbus.$emit('Presence-' + channelId, {
                        event: 'message',
                        data: e,
                    });
                })
                // eslint-disable-next-line no-console
                .error((error) => console.error(error));
        },
        leavePresence(channelId) {
            this.debugLog('Leaving presence channel: ' + channelId);
            sockets.leavePresenceChannel(channelId);
        },
        joinChannel(channelId) {
            this.debugLog('Joining private channel: ' + channelId);
            sockets.joinChannel(channelId).listenToAll((e) => {
                Eventbus.$emit('Private-' + channelId, {
                    event: 'message',
                    data: e,
                });
            });
        },
        leaveChannel(channelId) {
            this.debugLog('Leaving private channel: ' + channelId);
            sockets.leaveChannel(channelId);
        },
        storeUpdate(data) {
            log('store', { action: 'update', data });
            Object.keys(data).forEach((key) => {
                this.store.set(key, data[key]);
                if (key.startsWith('current_event.categories.')) {
                    const categoryId = key.split('.')[2] ?? null;
                    if (!categoryId) {
                        // eslint-disable-next-line no-console
                        console.error('Category ID not found in key: ', key);
                        return;
                    }
                    const category = this.store.get(`current_event.categories.${categoryId}`);
                    if (!category) {
                        // eslint-disable-next-line no-console
                        console.error('Category not found in store: ', categoryId);
                        return;
                    }
                    if (!category.is_revenue) {
                        recalculateDepartment(category.department_id);
                        return;
                    }
                    const revenueDepartment = this.store.get(
                        `current_event.revenue_departments.${category.department_id}`
                    );
                    if (!revenueDepartment) {
                        // eslint-disable-next-line no-console
                        console.error('Revenue department not found in store:', category.department_id);
                        return;
                    }
                    recalculateRevenueDepartment(revenueDepartment.id);
                }
            });
        },
        storeDelete(data) {
            log('store', { action: 'delete', data });
            data.forEach((key) => {
                this.store.delete(key);
            });
        },
        debugLog(message) {
            if (!this.debug) {
                return;
            }
            // eslint-disable-next-line no-console
            console.log(`[Sockets] ${message}`);
        },
        refetchEvent: debounce(function () {
            this.forceRefetchEvent();
        }, 2500),
        forceRefetchEvent() {
            if (!this.currentEvent) {
                return;
            }
            const scenario = this.store.get('current_scenario.id');
            axios
                .get(
                    route('api.account.event.single', {
                        resource: 1,
                        scenario: scenario ? scenario : null,
                    })
                )
                .then((response) => {
                    this.store.set('current_event', response.data);
                });
        },
        refetchAccount: debounce(function () {
            this.forceRefetchAccount();
        }, 5000),
        forceRefetchAccount() {
            axios
                .get(
                    route('api.account', {
                        resource: 1,
                    })
                )
                .then((response) => this.store.set('account', response.data));
        },
    },
};
</script>
